// src/pages/auth/VerifyEmailPage.jsx
//
// Lands here when the user clicks the email verification link from
// their inbox. Calls applyActionCode to mark the email verified, then
// shows one of three states: verifying / success / error. The success
// CTA navigates to /dashboard (universal home — /teams is coach-only
// and would bounce parent users via RequireCoachRole).
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '@/firebase/firebase';
import AuthShell, {
  AuthPill,
  AuthHeading,
  AuthSubtitle,
  AuthButton,
  AuthButtonSecondary,
} from '@/components/auth/AuthShell';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'

  useEffect(() => {
    if (!code) {
      setStatus('error');
      return;
    }
    applyActionCode(auth, code)
      .then(() => setStatus('success'))
      .catch((error) => {
        console.error('Verification failed', error);
        setStatus('error');
      });
  }, [code]);

  const handleContinue = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        // /dashboard is the universal team picker post-Phase-1.
        // /teams is coach-only and would bounce a parent through
        // RequireCoachRole. May 2026 fix.
        navigate('/dashboard');
        return;
      }
    }
    navigate('/login');
  };

  return (
    <AuthShell cardWidth="narrow">
      <div className="text-center">
        {status === 'verifying' && (
          <>
            <AuthPill>Verifying…</AuthPill>
            <div className="mx-auto mt-6 mb-2 h-12 w-12">
              <svg
                className="h-12 w-12 animate-spin text-[#b5f03d]"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.2" />
                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
            </div>
            <AuthHeading size="small">
              Confirming your <span className="text-[#b5f03d]">email…</span>
            </AuthHeading>
            <AuthSubtitle>
              Just a moment — we're activating your account.
            </AuthSubtitle>
          </>
        )}

        {status === 'success' && (
          <>
            <AuthPill>Email verified</AuthPill>
            {/* Lime check badge */}
            <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
              <svg
                className="h-8 w-8 text-[#b5f03d]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <AuthHeading size="small">
              You're <span className="text-[#b5f03d]">in.</span>
            </AuthHeading>

            <AuthSubtitle>
              Your account is now active. You can close this tab and return
              to the app, or continue here.
            </AuthSubtitle>

            <div className="mt-7">
              <AuthButton type="button" onClick={handleContinue}>
                Continue to Scrimly
              </AuthButton>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <AuthPill>Link issue</AuthPill>
            {/* Amber warning ring (not red — this is recoverable) */}
            <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15 ring-1 ring-amber-500/40">
              <svg
                className="h-8 w-8 text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <AuthHeading size="small">
              Link <span className="text-[#b5f03d]">expired.</span>
            </AuthHeading>

            <AuthSubtitle>
              This verification link is no longer valid. Sign in and we'll
              send you a fresh one.
            </AuthSubtitle>

            <div className="mt-7 space-y-3">
              <AuthButton type="button" onClick={() => navigate('/login')}>
                Back to sign in
              </AuthButton>
              <AuthButtonSecondary type="button" onClick={() => navigate('/register')}>
                Create a new account
              </AuthButtonSecondary>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}
